<template>
  <v-app transition="fade-transition">
    <v-card :class="`${isEditMode ? 'no-boxshadow' : ''}`" tile transition="fade-transition">
      <div :class="`${isCreateMode ? 'container container--fluid' : ''}`">
        <v-form ref="feedbackform" id="rate-card-form" v-model="valid" lazy-validation>
          <v-layout wrap justify-center mb-2>
            <v-flex :class="`content xs12 ${isCreateMode ? 'md7' : 'md12'}`">
              <v-row>
                <v-col cols="12" class="text-right pb-0">
                  <v-select
                    v-model="selectedLang"
                    item-text="title"
                    item-value="value"
                    :items="langOptions"
                    @change="changeLang()"
                    outlined
                    dense
                    style="max-width:100px;float:right;"
                  >
                    <template v-slot:item="{ item }">
                      <div><span :class="`fi ${item.icon}`"></span> {{ item.title }}</div>
                    </template>
                    <template v-slot:selection="{ item }">
                      <div><span :class="`fi ${item.icon}`"></span> {{ item.title }}</div>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <v-row v-if="isCreateMode">
                <v-col cols="12" class="text-center pb-0">
                  <div class="avatar-logo">
                    <a href="https://www.getkobe.com" target="_blank">
                      <img :src="logoURL" alt="KOBE Logo"/>
                    </a>
                  </div>
                </v-col>
                <v-col cols="12" class="pt-0">
                  <h2 class="form-title">{{formConfig.title}}</h2>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-col class="px-5 clearfix" cols="12">
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('name')}} <span class="text-danger">*</span></p>
                      <v-text-field
                        class="purple-input"
                        v-model="rateCardData.name"
                        type="textbox"
                        outlined
                        required
                        hide-details
                        :error-messages="$v.rateCardData.name.$error && `Name is required.`"
                        @input="$v.rateCardData.name.$touch()"
                      />
                    </v-col>
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('email')}}  <span class="text-danger">*</span></p>
                      <v-text-field
                        class="purple-input"
                        v-model="rateCardData.email"
                        type="email"
                        outlined
                        required
                        hide-details
                        :error-messages="$v.rateCardData.email.$error && `Email is required.`"
                        @input="$v.rateCardData.email.$touch()"
                        @keydown.space.prevent
                        @paste="preventPasteSpaces" 
                      />
                    </v-col>
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('dateOfBirth')}}</p>
                      <v-menu
                        ref="datePicker"
                        :close-on-content-click="false"
                        :return-value.sync="rateCardDOB"
                        v-model="DOBDateCal"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on }">
                          <v-text-field
                            v-model="rateCardData.dateOfBirth"
                            append-icon="event"
                            hide-details
                            outlined
                            v-on="on"
                            style="width:100%;"
                            readonly
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          id="rate-card-date-picker"
                          type="date"
                          :max="minBirthDate"
                          :show-current="minBirthDate"
                          v-model="rateCardData.dateOfBirth"
                        >
                          <v-spacer></v-spacer>
                          <v-btn
                              text
                              color="secondary"
                              @click="DOBDateCal = false"
                          >
                              Cancel
                          </v-btn>
                          <v-btn
                              text
                              color="secondary"
                              @click="changeDate"
                          >
                              OK
                          </v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('ethnicity')}}</p>
                      <v-select
                        class="pr-1"
                        :items="ethnicityOptions"
                        hide-details
                        outlined
                        v-model="rateCardData.ethnicity"
                      >
                      </v-select>
                      <v-text-field
                        v-if="rateCardData.ethnicity === 'Others'"
                        class="pr-1 mt-2"
                        outlined
                        hide-details
                        v-model="rateCardData.otherEth">
                      </v-text-field>
                    </v-col>
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('country')}} <span class="text-danger">*</span></p>
                      <v-select
                        class="pr-1"
                        :items="countries"
                        hide-details
                        outlined
                        v-model="rateCardData.country"
                        @change="changeCountry()"
                        required
                        :error-messages="$v.rateCardData.country.$error && `Country is required.`"
                        @input="$v.rateCardData.country.$touch()"
                      >
                      </v-select>
                    </v-col>
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('city')}}</p>
                      <v-combobox
                        class="pr-1"
                        :items="citiesByCountry"
                        hide-details
                        outlined
                        v-model="rateCardData.city"
                      >
                      </v-combobox>
                    </v-col>
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('gender')}} <span class="text-danger">*</span></p>
                      <v-select
                        class="purple-input"
                        :items="genderOptions"
                        v-model="rateCardData.gender"
                        type="textbox"
                        outlined
                        hide-details
                        required
                        :error-messages="$v.rateCardData.gender.$error && `Gender is required.`"
                        @input="$v.rateCardData.gender.$touch()"
                      ></v-select>
                    </v-col>
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('ContactNo')}}</p>
                      <v-layout wrap>
                        <v-flex row style="padding-top: 12px;padding-left: 15px;">
                          <v-select
                            style="max-width:100px;border-radius:5px 0 0 5px"
                            v-model="countryCode"
                            :items="countryCodeOptions"
                            outlined
                          ></v-select>
                          <v-text-field
                            style="border-radius:0 5px 5px 0"
                            class="purple-input"
                            v-model="rateCardData.contactNo"
                            type="textbox"
                            outlined
                            hide-details
                          />
                        </v-flex>
                      </v-layout>
                    </v-col>
                    <v-col class="col-xs-12 col-md-6">
                      <p class="h4 font-weight-bold mb-2">{{getLang('occupation')}}</p>
                      <v-text-field
                        class="purple-input"
                        v-model="rateCardData.occupation"
                        type="textbox"
                        outlined
                        hide-details
                      />
                    </v-col>
                    <!-- <v-col class="col-xs-12 col-md-6">
                      <v-radio-group v-model="rateCardData.type" row hide-details>
                        <v-radio
                          value="direct"
                          label="Direct"
                          class="justify-center" >
                        </v-radio>
                        <v-radio
                        value="agency"
                        label="Agency"
                        class="justify-center"></v-radio>
                      </v-radio-group>
                    </v-col>
                    <v-col class="col-xs-12 col-md-6" v-if="rateCardData.type === 'agency'">
                      <p class="h4 font-weight-bold mb-2">Agency Name</p>
                      <v-text-field
                        class="purple-input"
                        v-model="rateCardData.agencyName"
                        type="textbox"
                        outlined
                        hide-details
                      />
                    </v-col> -->
                  </v-col>
                  <v-col
                  :class="section.class ? section.class : 'xs12 md6'"
                  :style="section.style || ''"
                  v-for="(section, index) in formConfig.sections" :key="index">
                    <div
                      :class="[
                        'rate-card-platform-section',
                        {'rate-card-platform-section--common-fees': section.title === 'Common Fees'}
                      ]"
                    >
                      <h3 class="rate-card-platform-section__title">{{ section.title }}</h3>
                      <v-col 
                      :class="[
                        field.class ? field.class : 'xs12 md6',
                        {'rate-card-single-rate-field': field.type === 'number' && isStandaloneRateField(field.name)}
                      ]"
                      :style="field.style || ''"
                      v-for="(field, index) in section.fields" :key="index">
                        <p :class="`h4 font-weight-bold mb-2 ${field.labelClass || ''}`" 
                          v-if="field.label !== ''">
                          {{getLang(field.name)}} 
                          <span class="red--text" v-if="field.required">*</span>
                        </p>
                        <div
                          :class="[
                            'd-flex flex-column',
                            {'rate-card-single-rate-control': isStandaloneRateField(field.name)}
                          ]"
                          v-if="field.type === 'number'"
                        >
                          <div v-if="isEditMode && field.name === 'igStory'">
                            <p class="rate-label-title">Ordinary</p>
                          </div>
                          <div class="d-flex flex-row">
                            <v-select
                              class="currency-select"
                              style="margin-right: 5px;"
                              v-model.number="rateCardData.ordinaryRates[`${field.name}Currency`]"
                              :items="currenyOptions"
                              hide-details
                              dense
                              outlined
                            >
                            </v-select>
                            <v-text-field
                              :class="[
                                'purple-input',
                                {'rate-card-single-rate-value': isStandaloneRateField(field.name)}
                              ]"
                              v-model.number="rateCardData.ordinaryRates[field.name]"
                              type="number"
                              outlined
                              dense
                              :hide-details="isStandaloneRateField(field.name)"
                              :placeholder="field.placeholder ? field.placeholder : ''"
                            />
                          </div>
                        </div>
                        <div
                          class="d-flex flex-column"
                          v-if="field.type === 'number' && isEditMode && !isStandaloneRateField(field.name)"
                        >
                          <div v-if="field.name === 'igStory'">
                            <p class="rate-label-title pl-3">Preferential</p>
                          </div>
                          <div class="d-flex flex-row pl-3">
                            <v-select
                              class="currency-select"
                              style="margin-right: 5px;"
                              v-model.number="rateCardData.preferentialRates[`${field.name}Currency`]"
                              :items="currenyOptions"
                              hide-details
                              dense
                              outlined
                            >
                            </v-select>
                            <v-text-field
                              class="purple-input"
                              v-model.number="rateCardData.preferentialRates[field.name]"
                              type="number"
                              outlined
                              dense
                              :placeholder="field.placeholder ? field.placeholder : ''"
                            />
                          </div>
                        </div>
                        <v-text-field
                          class="purple-input"
                          v-model="rateCardData[field.name]"
                          type="textbox"
                          outlined
                          :style="field.fieldStyle || ''"
                          v-if="field.type === 'textbox'"
                          :placeholder="field.placeholder ? field.placeholder : ''"
                        />
                        <v-text-field
                          class="purple-input"
                          v-model="rateCardData[field.name]"
                          @keypress="onlyForNumber"
                          type="number"
                          outlined
                          :style="field.fieldStyle || ''"
                          v-if="field.type === 'normalNumber'"
                          :placeholder="field.placeholder ? field.placeholder : ''"
                        />
                        <v-textarea
                          filled
                          auto-grow
                          :hint="field.hint || ''"
                          :persistent-hint="field.hint ? true : false"
                          :placeholder="field.placeholder"
                          v-model="rateCardData[field.name]"
                          required
                          v-if="field.type === 'textarea'"
                          hide-details="auto"
                        ></v-textarea>
                        <custom-file-upload
                          :refresh="fileUploadRefresh"
                          :multiple="true"
                          @setMedias="updatePricingDecks"
                          v-if="field.type === 'file'"
                        />
                        <div v-if="field.type === 'multiple'">
                          <v-list v-for="(multipleOne, pIndex) in rateCardData[field.name]" :key="pIndex">
                            <v-list-item class="pa-0">
                              <v-list-item-content 
                                v-for="(multipleOption, oIndex) in field.options"
                                :key="oIndex" 
                                :style="multipleOption.style || ''">
                                <!-- {{multipleOption.options}} -->
                                <v-select
                                    class="platform-select pr-1"
                                    :items="multipleOption.options"
                                    item-value="value"
                                    item-text="label"
                                    hide-details
                                    v-model="rateCardData[field.name][pIndex][multipleOption.name]"
                                    :label="multipleOption.label"
                                    dense
                                    outlined
                                    v-if="multipleOption.type === 'select'"
                                    :style="multipleOption.style || ''"
                                  >
                                  </v-select>
                                  <v-text-field
                                    class="px-1 pt-5"
                                    dense
                                    outlined
                                    hide-details
                                    :placeholder="multipleOption.placeholder"
                                    v-model="rateCardData[field.name][pIndex][multipleOption.name]"
                                    v-if="multipleOption.type === 'textbox'"
                                    :style="multipleOption.style || ''">
                                  </v-text-field>
                                  <v-textarea
                                    rows="2"
                                    class="px-1 pt-5"
                                    dense
                                    outlined
                                    hide-details
                                    :placeholder="multipleOption.placeholder"
                                    v-model="rateCardData[field.name][pIndex][multipleOption.name]"
                                    v-if="multipleOption.type === 'textarea'"
                                    :style="multipleOption.style || ''">
                                  </v-textarea>
                                  <v-btn
                                    color="danger"
                                    @click="rateCardData[field.name].splice(pIndex, 1)"
                                    text
                                    x-small
                                    v-if="multipleOption.type === 'delete'">
                                    <v-icon color="red">mdi-delete-forever</v-icon>
                                  </v-btn>
                              </v-list-item-content>
                              <!-- <v-list-item-action>
                                <v-text-field solo v-model.number="no_of_tiktok_posts"></v-text-field>
                              </v-list-item-action> -->
                            </v-list-item>
                            <v-list v-if="rateCardData[field.name][pIndex][field.child.parentName] === 'others'">
                              <v-text-field
                                class="pb-2"
                                dense
                                outlined
                                hide-details
                                :placeholder="field.child.placeholder"
                                v-model="rateCardData[field.name][pIndex][field.child.name]">
                              </v-text-field>
                            </v-list>
                            <div class="d-flex flex-row">
                              <v-select
                                class="currency-select-multiple"
                                v-model.number="rateCardData.platforms[pIndex].rateCurrency"
                                :items="currenyOptions"
                                hide-details
                                dense
                                outlined
                              >
                              </v-select>
                              <v-text-field
                                class="px-1 multiple-rate"
                                style="max-width:150px;margin-left:6px;"
                                type="number"
                                dense
                                outlined
                                hide-details
                                placeholder="rate"
                                v-model.number="rateCardData.platforms[pIndex].rate">
                              </v-text-field>
                            </div>
                          </v-list>
                          <v-list class="float-right">
                            <v-list-item style="margin-top: 20px">
                              <v-btn
                              small
                              color="secondary"
                              @click="addPlatform(field.name)">
                              <v-icon small>mdi-plus</v-icon> Add
                              </v-btn>
                            </v-list-item>
                          </v-list>
                        </div>
                      </v-col>
                    </div>
                  </v-col>
                  <v-col class="px-5" cols="12">
                    <v-col class="col-xs-12 col-md-12">
                      <p class="h4 font-weight-bold mb-2">{{getLang('preferredCampaigns')}}</p>
                      <v-chip-group multiple active-class="secondary white--text" v-model="rateCardData.keywords" column required>
                        <v-chip v-for="(item, index) in categories" :key="index" :value="item"      
                          class="ma-2"                               
                          filter                                                                                                                                                             
                        >
                          {{item}}
                        </v-chip>
                      </v-chip-group>
                      <!-- <span class="red--text" v-if="rateCardData.keywords !== null && !rateCardData.keywords.length">{{getLang('preferredCampaigns')}}</span> -->
                    </v-col>
                    <v-col class="col-xs-12 col-md-12">
                      <p class="h4 font-weight-bold mb-2">{{getLang('bestRepresent')}} <span class="text-danger">*</span></p>
                      <!-- <v-text-field
                        class="purple-input"
                        v-model="rateCardData.aboutYou"
                        type="textbox"
                        outlined
                        :error-messages="$v.rateCardData.aboutYou.$error && `One liner about you is required.`"
                        @input="$v.rateCardData.aboutYou.$touch()"
                      /> -->
                      <v-textarea
                        filled
                        auto-grow
                        v-model="rateCardData.aboutYou"
                        required
                        hide-details="auto"
                        :error-messages="$v.rateCardData.aboutYou.$error && `This field is required.`"
                        @input="$v.rateCardData.aboutYou.$touch()"
                      ></v-textarea>
                    </v-col>

                    <v-col class="col-xs-12 col-md-12">
                      <p class="h4 font-weight-bold mb-2">{{getLang('loadContent')}}</p>
                      <v-radio-group v-model="rateCardData.loadContent">
                        <v-radio value="Yes" :label="getLang('yes')" class="justify-center" color="secondary"></v-radio>
                        <v-radio value="No" :label="getLang('no')" class="justify-center" color="secondary"></v-radio>
                      </v-radio-group>
                      <v-text-field
                        class="px-2"
                        style="max-width:300px"
                        dense
                        outlined
                        hide-details
                        v-model="rateCardData.loadContentText"
                        v-if="rateCardData.loadContent === 'No'">
                      </v-text-field>
                    </v-col>

                    <v-col class="col-xs-12 col-md-12">
                      <p class="h4 font-weight-bold mb-2">{{getLang('giftBrand')}}</p>
                      <v-radio-group v-model="rateCardData.giftBrand">
                        <v-radio value="Yes" :label="getLang('yes')" class="justify-center" color="secondary"></v-radio>
                        <v-radio value="No" :label="getLang('noPaidCampaign')" class="justify-center" color="secondary"></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-col>
                </v-col>
                <!-- <v-col class="px-5" cols="12">
                  <v-col class="col-xs-12 col-md-6">
                    <p class="h4 font-weight-bold mb-2">Country</p>
                    <v-select
                      class="pr-1"
                      :items="countries"
                      hide-details
                      outlined
                      v-model="rateCardData.country"
                    >
                    </v-select>
                  </v-col>
                  <v-col class="col-xs-12 col-md-6">
                    <v-radio-group class="mt-0" style="margin-bottom:10px" v-model="rateCardData.type" row hide-details>
                      <v-radio value="agency" label="Agency" class="justify-center"></v-radio>
                      <v-radio
                        value="direct"
                        label="Direct"
                        class="justify-center" >
                      </v-radio>
                    </v-radio-group>
                    <v-text-field
                      class="purple-input"
                      label="Agency Name"
                      v-if="rateCardData.type === 'agency'"
                      v-model="rateCardData.agencyName"
                      type="textbox"
                      outlined
                      hide-details
                    />
                  </v-col>
                </v-col> -->
              </v-row>
              <!-- End Section One -->

              
              <!-- required field messaage -->
              <v-row v-if="!hasStandaloneMediaHandle">
                <v-col cols="12" class="pb-0 px-5 mx-3">
                  <p class="text-danger font-weight-bold">{{getLang('enterIgTikTok')}} *</p>
                </v-col>
              </v-row>
              <!-- END required field messaage -->

              <v-row>
                <v-col cols="12" class="text-right">
                  <v-btn :disabled="submitting" class="secondary" x-large @click.prevent="submitForm">
                    <i class="fa fa-circle-o-notch fa-spin" v-if="submitting"></i> Submit
                  </v-btn>
                </v-col>
              </v-row>
              <!-- <v-row>
                <v-col>
                </v-col>
              </v-row> -->
            </v-flex>
          </v-layout>
        </v-form>
      </div>
    </v-card>
    <rate-card-terms-modal
      v-model="showAgreementModal"
      :value="showAgreementModal"
      :selectedLang="selectedLang"
      @termsAggrement="confirmSave"
    />
  </v-app>
</template>
<script>
import { mapState } from 'vuex';
import config from '@/config';
import rateCardConfig from './config';
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import CustomFileUpload from '../../../scoped/custom/CustomFileUpload';
import influencerHelper from '../influencerHelper'
// import influencerAPIs from '../influencerAPIs'
import utils from '@/utils'
import moment from 'moment';
import logoDataURL from '@/config/logoDataURL'
import countries from '../../../../../static/sources/countries';
import cities from '../../../../../static/sources/cities';
import dialCodes from '../../../../../static/sources/dialCodes';
import RateCardTermsModal from './RateCardTermsModal'
import 'flag-icons/css/flag-icons.min.css'

export default {
  name: 'RateCardForm',
  mixins: [validationMixin],
  validations: function() {
    const validationRules = {
      rateCardData: {
        name: {
          required
        },
        email: {
          required
        },
        country: {
          required
        },
        gender: {
          required
        },
        // loadContent: {
        //   required
        // },
        // occupation: {
        //   required
        // },
        aboutYou: {
          required
        }
        // keywords: {
        //   required
        // }
      }
    };
    // this.formConfig.sections.forEach(field => {
    //   if (field.fields) {
    //     field.fields.forEach(sectionField => {
    //       if (!sectionField.multiple) {
    //         validationRules['rateCardData'][sectionField.name] = sectionField.required ? {required} : '';
    //       }
    //     })
    //   }
    // })
    return validationRules
  },
  components: {
    CustomFileUpload,
    RateCardTermsModal
  },
  props: {
    mode: String,
    editData: Object
  },
  data () {
    return {
      dialog: true,
      rateCardDOB: false,
      formConfig: rateCardConfig.formConfig,
      langs: rateCardConfig.langs,
      countryCodeOptions: dialCodes.map(d => d.dc),
      logoURL: logoDataURL.logo || '/static/img/kobe_bg_main.png',
      genderOptions: [
        'Male',
        'Female'
      ],
      ethnicityOptions: [
        'Chinese',
        'Malay',
        'Indian',
        'Eurasian',
        'Others'
      ],
      rateCardData: {
        platforms: [],
        keywords: [],
        type: 'direct',
        agencyName: '',
        country: 'Singapore',
        city: '',
        ethnicity: '',
        otherEth: '',
        contactNo: '',
        gender: '',
        dateOfBirth: '',
        loadContent: '',
        loadContentText: '',
        giftBrand: '',
        igFollowerCount: '',
        tiktokFollowerCount: '',
        linkedinHandle: '',
        linkedinFollowerCount: '',
        twitterHandle: '',
        twitterFollowerCount: '',
        facebookHandle: '',
        facebookFollowerCount: '',
        youtubeHandle: '',
        youtubeFollowerCount: '',
        twitchHandle: '',
        twitchFollowerCount: '',
        // igAvgViews: '',
        // tiktokAvgViews: '',
        preferentialRates: {
          igStory: 0,
          igStoryCurrency: 'SGD',
          igPost: 0,
          igPostCurrency: 'SGD',
          igCarousel: 0,
          igCarouselCurrency: 'SGD',
          igVideo: 0,
          igVideoCurrency: 'SGD',
          igReel: 0,
          igReelCurrency: 'SGD',
          igTv: 0,
          igTvCurrency: 'SGD',
          loadingFee: 0,
          loadingFeeCurrency: 'SGD',
          eventAppearance: 0,
          eventAppearanceCurrency: 'SGD',
          tiktok: 0,
          tiktokCurrency: 'SGD',
          linkedin: 0,
          linkedinCurrency: 'SGD',
          twitter: 0,
          twitterCurrency: 'SGD',
          facebook: 0,
          facebookCurrency: 'SGD',
          youtube: 0,
          youtubeCurrency: 'SGD',
          twitch: 0,
          twitchCurrency: 'SGD'
        },
        ordinaryRates: {
          igStory: 0,
          igStoryCurrency: 'SGD',
          igPost: 0,
          igPostCurrency: 'SGD',
          igCarousel: 0,
          igCarouselCurrency: 'SGD',
          igVideo: 0,
          igVideoCurrency: 'SGD',
          igReel: 0,
          igReelCurrency: 'SGD',
          igTv: 0,
          igTvCurrency: 'SGD',
          loadingFee: 0,
          loadingFeeCurrency: 'SGD',
          eventAppearance: 0,
          eventAppearanceCurrency: 'SGD',
          tiktok: 0,
          tiktokCurrency: 'SGD',
          linkedin: 0,
          linkedinCurrency: 'SGD',
          twitter: 0,
          twitterCurrency: 'SGD',
          facebook: 0,
          facebookCurrency: 'SGD',
          youtube: 0,
          youtubeCurrency: 'SGD',
          twitch: 0,
          twitchCurrency: 'SGD'
        }
      },
      deckFiles: [],
      fileUploadRefresh: false,
      submitting: false,
      categories: [],
      countries: [],
      citiesByCountry: [],
      dialCodes,
      currenyOptions: rateCardConfig.currencyOptions,
      langOptions: [
        {
          icon: 'fi-gb-eng',
          title: 'EN',
          value: 'en'
        },
        {
          icon: 'fi-th',
          title: 'TH',
          value: 'th'
        },
        {
          icon: 'fi-vn',
          title: 'VN',
          value: 'vn'
        },
        {
          icon: 'fi-id',
          title: 'ID',
          value: 'id'
        },
        {
          icon: 'fi-cn',
          title: 'CN',
          value: 'cn'
        }
      ],
      selectedLang: 'en',
      minBirthDate: moment().subtract(13, 'years').format('YYYY-MM-DD'),
      DOBDateCal: false,
      showAgreementModal: false
    }
  },
  watch: {
    editData: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          const editData = utils.func.refreshJSON(this.editData)
          editData.platforms = Array.isArray(editData.platforms)
            ? editData.platforms.map(platform => Object.assign({}, platform, {
              platform: this.normalizePlatformKey(platform.platform)
            }))
            : []
          const standalonePlatforms = ['linkedin', 'twitter', 'facebook', 'youtube', 'twitch']
          const ordinaryRateDefaults = {}
          const preferentialRateDefaults = {}
          standalonePlatforms.forEach(platform => {
            const legacyPlatform = editData.platforms.find(item => item.platform === platform)
            const handleField = `${platform}Handle`
            editData[handleField] = editData[handleField] || this.extractPlatformHandle(legacyPlatform && legacyPlatform.url, platform)
            ordinaryRateDefaults[platform] = legacyPlatform ? legacyPlatform.rate : 0
            ordinaryRateDefaults[`${platform}Currency`] = legacyPlatform ? (legacyPlatform.rateCurrency || 'SGD') : 'SGD'
            preferentialRateDefaults[platform] = 0
            preferentialRateDefaults[`${platform}Currency`] = 'SGD'
          })
          editData.platforms = editData.platforms.filter(platform => !standalonePlatforms.includes(platform.platform))
          editData.ordinaryRates = Object.assign(ordinaryRateDefaults, editData.ordinaryRates || {})
          editData.preferentialRates = Object.assign(preferentialRateDefaults, editData.preferentialRates || {})
          this.rateCardData = editData
          if (this.rateCardData.contactNo) {
            // empty dial code and set country set option
            for (let code of this.countryCodeOptions) {
              if (this.rateCardData.contactNo.includes(code)) {
                this.rateCardData.contactNo = this.rateCardData.contactNo.replaceAll(code, '')
                this.countryCode = code
              }
            }
          }
        }
      }
    },
    selectedLang(newValue) {
      this.updateCategories(newValue)
    }
  },
  methods: {
    isStandaloneRateField(fieldName) {
      return ['tiktok', 'linkedin', 'twitter', 'facebook', 'youtube', 'twitch'].includes(fieldName)
    },
    normalizePlatformKey(platform) {
      const key = String(platform || '').trim().toLowerCase()
      return key === 'x' || key === 'x (twitter)' ? 'twitter' : key
    },
    extractPlatformHandle(value) {
      let handle = String(value || '').trim()
      if (!handle) return ''
      handle = handle.replace(/^@/, '').replace(/[?#].*$/, '').replace(/\/+$/, '')
      const urlMatch = handle.match(/(?:https?:\/\/)?(?:www\.)?(?:linkedin\.com\/in|x\.com|twitter\.com|facebook\.com|fb\.com|youtube\.com|youtu\.be|twitch\.tv)\/([^/]+)/i)
      if (urlMatch && urlMatch[1]) return decodeURIComponent(urlMatch[1]).replace(/^@/, '')
      if (handle.includes('/')) {
        const segments = handle.split('/').filter(Boolean)
        return segments[segments.length - 1] || ''
      }
      return handle
    },
    close() {
      this.$router.push({path: '/'})
    },
    // Prevent pasting spaces
    preventPasteSpaces(event) {
      let pastedText = (event.clipboardData || window.clipboardData).getData('text');
      if (/\s/.test(pastedText)) {
        event.preventDefault(); // Stops the paste action
      }
    },
    submitForm() {
      this.showAgreementModal = false
      this.$v.$touch()
      if (this.$v.rateCardData.$error) {
        return false
      }
      if (!this.hasStandaloneMediaHandle) {
        this.$swal({
          text: this.getLang('enterIgTikTok'),
          type: 'warning'
        });
        return
      }
      if (this.rateCardData.instagramHandle && !this.rateCardData.igFollowerCount) {
        this.$swal({
          text: 'Please enter Instagram Followers Count!',
          type: 'warning'
        });
        return
      }
      if (this.rateCardData.tiktokHandle && !this.rateCardData.tiktokFollowerCount) {
        this.$swal({
          text: 'Please enter TikTok Followers Count!',
          type: 'warning'
        });
        return
      }
      if (this.rateCardData.linkedinHandle && !this.rateCardData.linkedinFollowerCount) {
        this.$swal({
          text: 'Please enter Followers (Connections)!',
          type: 'warning'
        });
        return
      }
      if (this.rateCardData.twitterHandle && !this.rateCardData.twitterFollowerCount) {
        this.$swal({
          text: 'Please enter X (Twitter) Followers Count!',
          type: 'warning'
        });
        return
      }
      const followerRequirements = [
        ['facebook', 'Facebook'],
        ['youtube', 'YouTube'],
        ['twitch', 'Twitch']
      ]
      for (const [platform, label] of followerRequirements) {
        if (this.rateCardData[`${platform}Handle`] && !this.rateCardData[`${platform}FollowerCount`]) {
          this.$swal({
            text: `Please enter ${label} Followers Count!`,
            type: 'warning'
          });
          return
        }
      }
      // if (!this.rateCardData.keywords.length) {
      //   this.$swal({
      //     text: 'Please select preferred type of campaigns/genre!',
      //     type: 'warning'
      //   });
      //   return
      // }
      this.showAgreementModal = true
    },
    confirmSave() {
      this.$swal({
        title: 'Double Check!',
        text: 'Make sure you filled all the required information',
        confirmButtonText: 'Confirm',
        type: 'info',
        showCancelButton: true,
        showCloseButton: true
      }).then(async (result) => {
        if (result.value) {
          this.submitting = true;
          const formData = this.constructData();
          await this.$store.dispatch('influencer/saveRateCard', formData)
          if (this.savedRateCard) {
            // Dismiss modal by triggering the click event on Close button
            // this.close();
            // Update submitting status
            this.submitting = false;
            // Show success message
            this.$swal({
              text: `Rate Card ${this.rateCardData.id ? 'Updated' : 'Submitted'}`,
              type: 'success'
            }).then(result => {
              if (this.rateCardData.id) {
                this.$emit('updated')
              } else {
                this.$router.push({path: '/client'})
              }
            })
          } else {
            // Update submitting status
            this.submitting = false;
            this.$swal({
              text: 'Error saving rate card form.',
              type: 'error'
            });
          }
        }
      });
    },
    constructData() {
      let saveData = utils.func.refreshJSON(this.rateCardData);
      saveData.instagramHandle = saveData.instagramHandle ? utils.func.espaceHandleSign(utils.func.regexMatchIg(saveData.instagramHandle, config.regexPattern.instagram_profile)) : ''
      saveData.tiktokHandle = saveData.tiktokHandle ? utils.func.espaceHandleSign(utils.func.extractTikTokHandle(saveData.tiktokHandle)) : ''
      saveData.linkedinHandle = this.extractPlatformHandle(saveData.linkedinHandle, 'linkedin')
      saveData.twitterHandle = this.extractPlatformHandle(saveData.twitterHandle, 'twitter')
      saveData.facebookHandle = this.extractPlatformHandle(saveData.facebookHandle, 'facebook')
      saveData.youtubeHandle = this.extractPlatformHandle(saveData.youtubeHandle, 'youtube')
      saveData.twitchHandle = this.extractPlatformHandle(saveData.twitchHandle, 'twitch')
      saveData.platforms = (saveData.platforms || []).filter(platform => {
        const key = this.normalizePlatformKey(platform.platform)
        return key && !['linkedin', 'twitter', 'facebook', 'youtube', 'twitch'].includes(key)
      })
      saveData.contactNo = `${this.countryCode}${saveData.contactNo}`
      let formData = new FormData();
      formData.append('data', JSON.stringify(saveData))
      for (let val of this.deckFiles) {
        formData.append('uploadFile', val);
      }
      return formData;
    },
    updatePricingDecks(medias) {
      this.deckFiles = Object.assign([], medias)
    },
    addPlatform(fieldName) {
      this.rateCardData[fieldName].push({
        platform: '',
        url: '',
        rate: '',
        rateCurrency: 'SGD'
      })
    },
    changeDate() {
      this.$refs.datePicker.save(this.rateCardData.dateOfBirth);
    },
    changeCountry() {
      let optionOne = this.dialCodes.find(o => o.cn === this.rateCardData.country)
      if (optionOne) {
        this.countryCode = optionOne.dc
      }
      this.rateCardData.city = ''
      // find city
      this.citiesByCountry = []
      let selectedCities = cities[this.rateCardData.country]
      if (Array.isArray(selectedCities)) {
        this.citiesByCountry = Object.assign([], selectedCities)
      }
    },
    getLang(attr) {
      return this.langs[this.selectedLang][attr] || this.langs.en[attr] || ''
    },
    updateCategories(newValue) {
      this.categories = influencerHelper.getCategories(newValue)
    },
    setLangParam() {
      let queries = this.$route.query
      queries.lang = this.selectedLang
      this.$router.replace({ query: queries })
    },
    changeLang() {
      this.setLangParam()
    },
    onlyForNumber($event) {
      // console.log($event.keyCode); //keyCodes value
      let keyCode = ($event.keyCode ? $event.keyCode : $event.which);
      // only allow number
      if (keyCode < 48 || keyCode > 57) { // 46 is dot
        $event.preventDefault();
      }
    }
  },
  computed: {
    ...mapState({
      savedRateCard: state => state.influencer.savedRateCard
    }),
    currentUserID: function () {
      return this.$store.state.user.id
    },
    isCreateMode: function () {
      return !this.mode
    },
    isEditMode: function () {
      return this.mode === 'edit'
    },
    hasStandaloneMediaHandle: function () {
      return Boolean(
        this.rateCardData.instagramHandle ||
        this.rateCardData.tiktokHandle ||
        this.rateCardData.linkedinHandle ||
        this.rateCardData.twitterHandle ||
        this.rateCardData.facebookHandle ||
        this.rateCardData.youtubeHandle ||
        this.rateCardData.twitchHandle
      )
    }
  },
  mounted() {
    if (this.$route.query.lang) {
      this.selectedLang = this.$route.query.lang
    }
    if (this.$route.query.name) {
      this.rateCardData.name = this.$route.query.name
    }
    if (this.$route.query.email) {
      this.rateCardData.email = this.$route.query.email
    }
    if (this.$route.query.influencerID) {
      this.rateCardData.influencerID = this.$route.query.influencerID
    }
    if (!this.rateCardData.platforms.length) {
      this.addPlatform('platforms')
    }
    // Retrieve countries manually
    this.countries = countries
    this.countries = this.countries.map(c => c.name)
    this.changeCountry()
    this.updateCategories(this.selectedLang)
    this.setLangParam()
  }
}
</script>
<style lang="scss">
  .v-dialog--fullscreen {
    scroll-behavior: smooth;
  }
  #rate-card-form .multiple-rate .v-text-field__prefix {
    font-size: 11px;
  }

  #rate-card-form .currency-select .v-input__slot {
    padding: 0;
    padding-left: 5px;
  }
  #rate-card-form .currency-select-multiple {
    min-width: 115px;
    max-width: 115px;
  }
  #rate-card-form .more-platform-label {
    margin-bottom: -15px!important;
  }
  #rate-card-form .rate-card-platform-section {
    height: 100%;
    padding: 18px 14px 8px;
    border: 1px solid #e1e4ec;
    border-radius: 10px;
    background: #fff;
  }
  #rate-card-form .rate-card-platform-section__title {
    margin: 0 12px 10px;
    color: #5145ff;
    font-size: 18px;
    font-weight: 700;
  }
  #rate-card-form .rate-card-platform-section--common-fees {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    padding-bottom: 18px;
  }
  #rate-card-form .rate-card-platform-section--common-fees .rate-card-platform-section__title {
    flex: 0 0 100%;
    margin-bottom: 22px;
  }
  #rate-card-form .rate-card-platform-section--common-fees > .v-col {
    float: none;
    padding-top: 0 !important;
  }
  #rate-card-form .rate-card-single-rate-field {
    display: grid !important;
    grid-template-columns: minmax(140px, 1fr) minmax(0, 2fr);
    align-items: center;
    column-gap: 16px;
    padding-top: 8px !important;
    padding-bottom: 16px !important;
  }
  #rate-card-form .rate-card-single-rate-field > p {
    min-width: 0 !important;
    margin: 0 !important;
  }
  #rate-card-form .rate-card-single-rate-control,
  #rate-card-form .rate-card-single-rate-control > .d-flex {
    min-width: 0;
    width: 100%;
  }
  #rate-card-form .rate-card-single-rate-control .currency-select {
    flex: 0 0 112px;
    width: 112px;
    min-width: 112px;
    max-width: 112px;
  }
  #rate-card-form .rate-card-single-rate-value {
    flex: 1 1 auto;
    min-width: 0;
  }
  #rate-card-form .rate-card-single-rate-control .v-input__slot {
    min-height: 40px !important;
    margin-bottom: 0 !important;
  }
  @media (max-width: 900px) {
    #rate-card-form .rate-card-single-rate-field {
      grid-template-columns: 1fr;
      row-gap: 8px;
    }
  }
  #rate-card-date-picker .v-picker__actions.v-card__actions {
    background: #fff!important;
  }
</style>
<style scoped>
  html {
    overflow-y: hidden!important;
  }

  .bg-soft-grey {
    background-color: #f6f6f6;
  }

  .bg--white {
    background-color: #fff;
  }

  .v-text-field-sm{
    width: 320px;
  }

  .form-label {
    min-height: 22px;
  }

  .caption-text {
    color: #f79d18;
  }

  /* .td-error {
    margin-bottom: -20px;
    right: 16px;
  } */

  .td-error, .alert-error {
    display: inline-block;
    font-size: 12px;
    right: 15px;
    padding: 5px;
    margin-bottom: 0px;
    color: #fff!important;
  }

  .alert-error {
    right: 0
  }

  .checkboxes-error {
    margin-top: -15px;
  }

  .d-sm-table-cell {
    height: auto!important;
    padding: 0;
    padding-left: 15px;
  }

  .v-card {
    background-color: rgb(204, 204, 204)!important;
  }

  .content {
    max-width: 100%!important;
    background-color: #fff;
    padding: 20px;
  }

  .avatar-logo {
    width: 120px!important;
    min-width: 120px!important;
    height: auto!important;
    transition: all 0.5s ease-out;
    margin: 0 auto;
  }

  .avatar-logo img {
    width: 120px;
  }
  
  .form-title {
    margin-top: 0;
    text-align: center;
    font-weight: 600;
    /* padding: 40px; */
    transition: all 0.5s ease-out;
  }

   .label-stats {
    min-width: 150px;
    margin:0px;
  }

  .edit-rate-card-form .label-stats {
    min-width: 150px;
  }


  .edit-rate-card-form .rate-label-title {
    margin-top:-10px;
    font-weight: bold;
  }

  .event-att-section {
    margin-top: 100px;
  }

   .currency-select {
    width: 100px;
    font-size: 12px;
  }

  .no-boxshadow {
    box-shadow: none!important;
  }

  @media (max-width: 768px) {
    .content {
      padding: 20px;
    }
  }
  
  @media (max-width: 600px) {
    .content {
      padding: 0;
    }
    .avatar-logo {
      height: auto!important;
      min-width: 100%!important;
      width: 100%!important;
    }
    
    .form-title {
      float: none;
      padding: 0;
      font-size: 25px;
      width: 100%;
      text-align: center;
    }

    .float-xs-left {
      float: left;
    }

    .td-error, .alert-error {
      width: 100%;
      right: 0;
    }

    .pt-xs-5 {
      padding-top: 15px
    }

    .mb-xs-0 {
      margin-bottom: 0;
    }
  }

  /* @media (max-width: 400px) {
    .platform-select {
      min-width: 50px!important;
      max-width: 50px!important;
    }
  } */

  @media (min-width: 1000px) and (max-width: 1366px) {
    .content {
      flex-basis: 90%!important;
    }
  }
</style>



// WEBPACK FOOTER //
// Form.vue?bd9f0af0